/* eslint-disable max-lines */
// deps
import { BILL_TIMEOUT_ERROR } from "constants/bill";
import { useCallback, useContext, useEffect, useRef } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import {
    BottomBarContext,
    isApp,
    QrCodeActions,
    ResponseContext,
    SwiggyContextHeader,
    TableContext,
    usePlApiContextFetch,
} from "@swiggy-private/portal-dineout-context";
// constants
import {
    API_ENDPOINTS,
    BILL_POLL_BASE_DELAY_SECONDS,
    BILL_POLL_MAX_DELAY_SECONDS,
    BILL_POLL_MAX_TOTAL_MS,
    BILL_SUMMARY_SUCCESS_STATUS_CODES,
    DINEOUT_CART_API_HOST,
    LOG_TABLES,
} from "../constants";

// utils
import { getDefaultApiHeaders, sendCustomNREvViaBlaze } from "../utils";

// useBillSummary: Fetches the bill and manages a linear-backoff poller.
// Poll schedule: starts at BILL_POLL_BASE_DELAY_SECONDS, increments by that amount
// per tick, capped at BILL_POLL_MAX_DELAY_SECONDS; hard-stops after BILL_POLL_MAX_TOTAL_MS
// and dispatches BILL_SUMMARY_ERROR.
export const useBillSummary = () => {
    const fetchPL = usePlApiContextFetch();
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { tableId = "", restaurantId = "", parentOrderId = "" } = useContext(TableContext);
    const { dispatch } = useContext(ResponseContext);
    const { setIsVisible } = useContext(BottomBarContext);
    const hasFetchedRef = useRef(false);
    const pollingRef = useRef<number | null>(null);
    const isPollingInFlightRef = useRef(false);
    const currentDelayMsRef = useRef<number>(BILL_POLL_BASE_DELAY_SECONDS * 1000);
    const pollingStartTimeRef = useRef<number | null>(null);

    // Cancels any scheduled poll timeout and resets poll tracking refs
    const stopBillPolling = useCallback(() => {
        if (pollingRef.current) {
            clearTimeout(pollingRef.current);
            pollingRef.current = null;
        }
        pollingStartTimeRef.current = null;
    }, []);

    // fetchBill
    // Executes the bill fetch and updates ResponseContext state based on result
    const fetchBill = useCallback(
        async (opts?: {
            showLoading?: boolean;
            retryCount?: number;
            sendEvent?: typeof sendCustomNREvViaBlaze;
            isNewRequest?: boolean;
        }) => {
            const showLoading = opts?.showLoading ?? false;
            const retryCount = opts?.retryCount ?? 1;
            const sendEvent = opts?.sendEvent ?? sendCustomNREvViaBlaze;

            try {
                if (showLoading) {
                    dispatch({ type: QrCodeActions.BILL_SUMMARY_LOADING, payload: {} });
                }

                const data = await fetchPL?.<{ cards: IPLWidgetControllerCards[] }>(
                    {
                        apiHost: DINEOUT_CART_API_HOST,
                        endpoint: API_ENDPOINTS.GET_BILL,
                        retryCount,
                        bodyParams: { tableId, restaurantId, parentOrderId },
                        searchParams: { ...(opts?.isNewRequest ? { isNewRequest: String(opts?.isNewRequest) } : {}) },
                        options: {
                            method: "POST",
                            headers: {
                                ...swiggyHeaders,
                                ...getDefaultApiHeaders(swiggyHeaders),
                                platform: isApp() ? swiggyHeaders?.platform : "Swiggy-Mweb",
                                // hardcoding agent-type to platform for cart APIs, for PL APIs we need to set agent-type = webview (change done in getDefaultApiHeaders)
                                "agent-type": isApp() ? swiggyHeaders?.platform : "Swiggy-Mweb",
                            } as HeadersInit,
                        },
                    },
                    { ...swiggyHeaders, ...getDefaultApiHeaders(swiggyHeaders) },
                    sendEvent,
                    LOG_TABLES,
                );

                if (!isEmpty(data) && data?.success && BILL_SUMMARY_SUCCESS_STATUS_CODES.includes(data?.statusCode)) {
                    dispatch({ type: QrCodeActions.BILL_SUMMARY_SUCCESS, payload: { data } });
                    return true;
                }

                dispatch({ type: QrCodeActions.BILL_SUMMARY_ERROR, payload: {} });
                stopBillPolling();
                return false;
            } catch (_) {
                dispatch({ type: QrCodeActions.BILL_SUMMARY_ERROR, payload: {} });
                stopBillPolling();
                return false;
            }
        },
        [dispatch, fetchPL, parentOrderId, restaurantId, stopBillPolling, swiggyHeaders, tableId],
    );

    // getBill
    // One-shot fetch with loading and retry semantics used on initial mount
    const getBill = useCallback(
        async (isNewRequest?: boolean, showLoading?: boolean) => {
            const ok = await fetchBill({ showLoading: showLoading ?? true, retryCount: 3, isNewRequest });
            if (!ok) hasFetchedRef.current = false;
        },
        [fetchBill],
    );

    //Hides the bottom bar while the bill loads
    useEffect(() => {
        setIsVisible(false);

        return () => {
            setIsVisible(true);
        };
    }, [setIsVisible]);

    // triggers initial getBill once
    useEffect(() => {
        if (!swiggyHeaders || isEmpty(tableId) || isEmpty(restaurantId)) return;

        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        getBill();
    }, [getBill, restaurantId, swiggyHeaders, tableId]);

    // Stops any ongoing polling and re-fetches the bill (optionally isNewRequest)
    const retryGetBill = useCallback(
        async (isNewRequest?: boolean, showLoading?: boolean) => {
            // if not user generated request, stop polling
            if (!isNewRequest) {
                stopBillPolling();
            }
            await getBill(isNewRequest, showLoading);
        },
        [getBill, stopBillPolling],
    );
    // Single polling tick with in-flight guard and table/header presence checks
    const poll = useCallback(async () => {
        if (isPollingInFlightRef.current) return;
        isPollingInFlightRef.current = true;
        if (!swiggyHeaders || isEmpty(tableId) || isEmpty(restaurantId)) {
            isPollingInFlightRef.current = false;
            return;
        }
        try {
            await fetchBill({ showLoading: false, retryCount: 1 });
        } catch (_) {
            stopBillPolling();
            dispatch({ type: QrCodeActions.BILL_SUMMARY_ERROR, payload: {} });
        } finally {
            isPollingInFlightRef.current = false;
        }
    }, [swiggyHeaders, tableId, restaurantId, fetchBill, stopBillPolling, dispatch]);

    // Schedules the next poll via setTimeout and increments delay linearly (<= max)
    const scheduleNext = useCallback(() => {
        if (pollingStartTimeRef.current === null) return; // stopped
        const elapsedMs = Date.now() - pollingStartTimeRef.current;
        if (elapsedMs >= BILL_POLL_MAX_TOTAL_MS) {
            stopBillPolling();
            dispatch({
                type: QrCodeActions.BILL_SUMMARY_ERROR,
                payload: {
                    errorInfo: BILL_TIMEOUT_ERROR,
                },
            });
            return;
        }
        pollingRef.current = window.setTimeout(async () => {
            if (pollingStartTimeRef.current === null) return; // stopped
            const tickElapsedMs = Date.now() - pollingStartTimeRef.current;
            if (tickElapsedMs >= BILL_POLL_MAX_TOTAL_MS) {
                stopBillPolling();
                dispatch({
                    type: QrCodeActions.BILL_SUMMARY_ERROR,
                    payload: {
                        errorInfo: BILL_TIMEOUT_ERROR,
                    },
                });
                return;
            }
            await poll();
            const nextDelayMs = Math.min(
                currentDelayMsRef.current + BILL_POLL_BASE_DELAY_SECONDS * 1000,
                BILL_POLL_MAX_DELAY_SECONDS * 1000,
            );
            currentDelayMsRef.current = nextDelayMs;
            scheduleNext();
        }, currentDelayMsRef.current);
    }, [dispatch, poll, stopBillPolling]);

    // Idempotently starts polling from base delay; does nothing if already started
    const startBillPolling = useCallback(() => {
        // Idempotent: if already started, do nothing
        if (pollingStartTimeRef.current !== null || pollingRef.current) return;
        pollingStartTimeRef.current = Date.now();
        currentDelayMsRef.current = BILL_POLL_BASE_DELAY_SECONDS * 1000;
        scheduleNext();
    }, [scheduleNext]);

    useEffect(() => {
        return () => {
            stopBillPolling();
        };
    }, [stopBillPolling]);

    return { getBill, retryGetBill, startBillPolling, stopBillPolling };
};
